import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import RemoveShoppingCartOutlinedIcon from '@mui/icons-material/RemoveShoppingCartOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { DataTable, DataTableColumn, EmptyState, Notice, PageHeader, StatCard, StatusChip, StatusTone } from '@routes-plan/ui';
import StandardModal, { useModalLoading } from '../../../components/modales/StandardModal';
import StockReceiptNew from '../receipts/StockReceiptNew';
import ProductAssignments from './ProductAssignments';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import LocationPicker, { isLocationComplete, toApiLocation } from '../shared/LocationPicker';
import {
  INVENTORY_ROUTES,
  OfficeOption,
  ReadinessResponse,
  ReadinessRow,
  ReadinessStatus,
  ShowAlerts,
  apiErrorMessage,
  fetchOffices,
  fetchReadiness,
  formatMoney,
  newRequestId,
} from '../shared/inventoryApi';
import { ActionIcon, IntField, PageBody, SaveButton, useInventoryAlert } from '../shared/ui';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import { TextKey, useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';

type Loc = { officeId: number | null; routeId: number | null };

// El texto de cada estado va en el archivo de idioma (labelKey).
const STATUS_INFO: Record<ReadinessStatus, { labelKey: TextKey; tone: StatusTone; order: number }> = {
  NEEDS_TRANSFER: { labelKey: 'products.status.needsTransfer', tone: 'warning', order: 0 },
  NO_STOCK: { labelKey: 'products.status.noStock', tone: 'danger', order: 1 },
  NOT_ENABLED: { labelKey: 'products.status.notEnabled', tone: 'neutral', order: 2 },
  DISABLED: { labelKey: 'products.status.disabled', tone: 'neutral', order: 3 },
  READY: { labelKey: 'products.status.ready', tone: 'success', order: 4 },
  INACTIVE: { labelKey: 'products.status.inactive', tone: 'neutral', order: 5 },
};

/**
 * Productos por ruta: en un solo lugar, qué se puede prestar en una ruta (o sede),
 * qué falta para cada producto y el botón para resolverlo.
 */
const ProductsByRoute: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const navigate = useNavigate();
  const showAlert = useInventoryAlert();
  const { canUpdate } = useResourcePermissions(INVENTORY_ROUTES.products);
  const { canCreate: canCreateIncome } = useResourcePermissions(INVENTORY_ROUTES.incomes);
  const { canTransfer } = useResourcePermissions(INVENTORY_ROUTES.transfers);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [location, setLocation] = useState<Loc>({ officeId: null, routeId: null });
  const [info, setInfo] = useState<ReadinessResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [onlyPending, setOnlyPending] = useState(false);
  const [transferRow, setTransferRow] = useState<ReadinessRow | null>(null);
  // Ingresar unidades y Asignar rutas se abren en el modal estándar sobre la tabla.
  const [incomeRow, setIncomeRow] = useState<ReadinessRow | null>(null);
  const [assignRow, setAssignRow] = useState<ReadinessRow | null>(null);
  const incomeLoading = useModalLoading();
  const assignLoading = useModalLoading();
  setPageTitle('products.byRoute.title');

  useEffect(() => {
    fetchOffices().then(setOffices).catch(() => setOffices([]));
  }, []);

  const load = useCallback(async () => {
    if (!isLocationComplete(location)) {
      setInfo(null);
      return;
    }
    setLoading(true);
    try {
      setInfo(await fetchReadiness(toApiLocation(location)));
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.loadError')), 'error');
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [location, showAlert, t]);

  useEffect(() => {
    load();
  }, [load]);

  const isRoute = Boolean(location.routeId);
  const placeName = info
    ? isRoute
      ? t('products.byRoute.placeRoute', { name: info.routeName ?? '' })
      : t('products.byRoute.placeOffice', { name: info.officeName ?? '' })
    : '';

  const enableHere = async (row: ReadinessRow) => {
    showLoading();
    try {
      await axios.put(`products/${row.productId}/assignments`, {
        assignments: [{ ...(isRoute ? { routeId: location.routeId } : { officeId: location.officeId }), active: true }],
      });
      showAlert(t('products.byRoute.enabled', { product: row.title, place: placeName }), 'success');
      await load();
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.byRoute.enableError')), 'error');
    } finally {
      hideLoading();
    }
  };

  const rows = useMemo(
    () =>
      (info?.data || [])
        .filter((r) => r.status !== 'INACTIVE')
        .filter((r) => !onlyPending || r.status === 'NEEDS_TRANSFER' || r.status === 'NO_STOCK')
        .sort((a, b) => STATUS_INFO[a.status].order - STATUS_INFO[b.status].order || a.title.localeCompare(b.title, 'es')),
    [info, onlyPending],
  );

  /** Acción sugerida según el estado. */
  const actionOf = (row: ReadinessRow): { label: string; icon: React.ReactNode; onClick: () => void; primary?: boolean } | null => {
    if (row.status === 'NEEDS_TRANSFER' && canTransfer) {
      return { label: t('products.action.transfer'), icon: <LocalShippingOutlinedIcon fontSize="small" />, onClick: () => setTransferRow(row), primary: true };
    }
    if (row.status === 'NO_STOCK' && canCreateIncome) {
      return { label: t('products.action.income'), icon: <AddBoxOutlinedIcon fontSize="small" />, onClick: () => setIncomeRow(row), primary: true };
    }
    if ((row.status === 'NOT_ENABLED' || row.status === 'DISABLED') && canUpdate) {
      return { label: t('products.action.enable'), icon: <PlaylistAddCheckOutlinedIcon fontSize="small" />, onClick: () => enableHere(row) };
    }
    if (canUpdate) {
      return { label: t('products.assignRoute'), icon: <AltRouteOutlinedIcon fontSize="small" />, onClick: () => setAssignRow(row) };
    }
    return null;
  };

  const columns: DataTableColumn<ReadinessRow>[] = [
    { id: 'title', header: t('products.product'), value: (r) => r.title, sortable: true },
    { id: 'sku', header: t('products.sku'), value: (r) => r.sku ?? '', hideBelow: 'sm' },
    { id: 'price', header: t('products.price'), align: 'right', sortable: true, value: (r) => Number(r.price), render: (r) => formatMoney(r.price) },
    { id: 'officeAvailable', header: t('products.byRoute.inOffice'), align: 'right', sortable: true, value: (r) => r.officeAvailable },
    ...(isRoute
      ? [{ id: 'routeAvailable', header: t('products.byRoute.inRoute'), align: 'right' as const, sortable: true, value: (r: ReadinessRow) => r.routeAvailable }]
      : []),
    {
      id: 'status',
      header: t('common.status'),
      sortable: true,
      value: (r) => t(STATUS_INFO[r.status].labelKey),
      render: (r) => <StatusChip label={t(STATUS_INFO[r.status].labelKey)} tone={STATUS_INFO[r.status].tone} />,
    },
    { id: 'action', header: t('products.byRoute.action'), value: (r) => actionOf(r)?.label ?? '', hideBelow: 'md' },
  ];

  const summary = info?.summary;
  const hasLocation = isLocationComplete(location);

  return (
    <PageBody>
      <PageHeader
        title={t('products.byRoute.title')}
        description={t('products.byRoute.intro')}
        breadcrumbs={[{ label: t('products.list.title'), onClick: () => navigate(INVENTORY_ROUTES.products) }, { label: t('products.byRoute.title') }]}
      />
      <Card>
        <CardContent sx={{ display: 'grid', gap: 1 }}>
          <LocationPicker offices={offices} value={location} onChange={setLocation} />
          <FormControlLabel
            control={<Switch checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} disabled={!hasLocation} />}
            label={t('products.byRoute.onlyPending')}
            sx={{ justifySelf: 'start' }}
          />
        </CardContent>
      </Card>
      {info && !info.allowProductLoans && <Notice severity="info">{t('products.byRoute.financialOffice', { office: info.officeName ?? '' })}</Notice>}
      {summary && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))' }}>
          <StatCard label={t('products.byRoute.ready')} value={summary.ready} icon={<CheckCircleOutlineOutlinedIcon />} loading={loading} />
          {isRoute && <StatCard label={t('products.byRoute.needsTransfer')} value={summary.needsTransfer} icon={<PendingActionsOutlinedIcon />} loading={loading} />}
          <StatCard label={t('products.byRoute.noStock')} value={summary.noStock} icon={<RemoveShoppingCartOutlinedIcon />} loading={loading} />
          <StatCard label={t('products.byRoute.notEnabled')} value={summary.notEnabled} icon={<BlockOutlinedIcon />} loading={loading} />
        </Box>
      )}
      <Card>
        <CardContent>
          {!hasLocation ? (
            <EmptyState icon={<PlaceOutlinedIcon fontSize="inherit" />} title={t('products.byRoute.selectLocation')} description={t('products.byRoute.selectLocationHint')} />
          ) : (
            <DataTable<ReadinessRow>
              rows={rows}
              columns={columns}
              getRowId={(r) => r.productId}
              loading={loading}
              ariaLabel={t('products.byRoute.title')}
              searchPlaceholder={t('products.searchPlaceholder')}
              exportFileName={t('products.byRoute.exportFile')}
              rowActions={(r) => {
                const a = actionOf(r);
                return a ? <ActionIcon label={a.label} icon={a.icon} onClick={a.onClick} color={a.primary ? 'primary' : 'default'} /> : null;
              }}
              emptyState={
                <EmptyState
                  dense
                  icon={<CheckCircleOutlineOutlinedIcon fontSize="inherit" />}
                  title={onlyPending ? t('products.byRoute.noPending') : t('products.byRoute.noProducts')}
                />
              }
            />
          )}
        </CardContent>
      </Card>
      {transferRow && info && (
        <TransferDialog
          row={transferRow}
          officeId={info.officeId}
          routeId={Number(location.routeId)}
          routeName={info.routeName || ''}
          onClose={(done) => {
            setTransferRow(null);
            if (done) load();
          }}
          showAlert={showAlert}
          showLoading={showLoading}
          hideLoading={hideLoading}
        />
      )}
      <StandardModal open={Boolean(incomeRow)} onClose={() => setIncomeRow(null)} title={t('products.byRoute.incomeTitle')} subtitle={incomeRow?.title} maxWidth={1000} loading={incomeLoading.loading} composed>
        {incomeRow && (
          <StockReceiptNew
            key={`income-${incomeRow.productId}`}
            variant="modal"
            initialLocation={{ officeId: location.officeId, routeId: location.routeId }}
            initialProductId={incomeRow.productId}
            onClose={() => setIncomeRow(null)}
            onSaved={() => {
              setIncomeRow(null);
              load();
            }}
            showAlert={showAlert}
            showLoading={incomeLoading.showLoading}
            hideLoading={incomeLoading.hideLoading}
          />
        )}
      </StandardModal>
      <StandardModal open={Boolean(assignRow)} onClose={() => setAssignRow(null)} title={t('products.assignRoute')} subtitle={assignRow?.title} maxWidth={1000} loading={assignLoading.loading} composed>
        {assignRow && (
          <ProductAssignments
            key={`assign-${assignRow.productId}`}
            variant="modal"
            productId={assignRow.productId}
            onClose={() => setAssignRow(null)}
            onChanged={load}
            showAlert={showAlert}
            showLoading={assignLoading.showLoading}
            hideLoading={assignLoading.hideLoading}
          />
        )}
      </StandardModal>
    </PageBody>
  );
};

interface TransferDialogProps extends ShowAlerts {
  row: ReadinessRow;
  officeId: number;
  routeId: number;
  routeName: string;
  onClose: (done: boolean) => void;
}

/** Envío rápido sede → ruta de un producto (mismo traslado de Inventario › Traslados). */
const TransferDialog: React.FC<TransferDialogProps> = ({ row, officeId, routeId, routeName, onClose, showAlert }) => {
  const t = useT();
  // El Cargando general queda detrás del modal: se usa el del modal.
  const modalLoading = useModalLoading();
  const { canTransfer } = useResourcePermissions(INVENTORY_ROUTES.transfers);
  const [quantity, setQuantity] = useState(String(row.officeAvailable));
  const [saving, setSaving] = useState(false);
  const [requestId] = useState(() => newRequestId('transfer'));
  const qty = Number(quantity);
  const valid = Number.isInteger(qty) && qty > 0 && qty <= row.officeAvailable;

  const send = async () => {
    if (!valid) return;
    setSaving(true);
    modalLoading.showLoading();
    try {
      await axios.post('stock-transfers', {
        origin: { officeId: officeId },
        destination: { routeId: routeId },
        date: nowOperationalDateString(),
        observation: t('products.transfer.note'),
        clientRequestId: requestId,
        items: [{ productId: row.productId, quantity: qty }],
      });
      showAlert(t('products.transfer.sent', { qty, product: row.title, route: routeName }), 'success');
      onClose(true);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.transfer.error')), 'error');
    } finally {
      setSaving(false);
      modalLoading.hideLoading();
    }
  };

  return (
    <StandardModal
      open
      loading={modalLoading.loading}
      onClose={() => onClose(false)}
      disableClose={saving}
      title={t('products.action.transfer')}
      subtitle={`${row.title} · ${routeName}`}
      maxWidth={480}
      actions={
        <>
          <Button color="inherit" variant="outlined" onClick={() => onClose(false)}>
            {t('common.cancel')}
          </Button>
          <SaveButton allowed={canTransfer} deniedTooltip={t('inventory.transfers.noPermission')} loading={saving} disabled={!valid} onClick={send}>
            {t('products.transfer.send')}
          </SaveButton>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('products.transfer.available', { count: row.officeAvailable })}
      </Typography>
      <IntField
        autoFocus
        fullWidth
        label={t('products.unitsToSend')}
        value={quantity}
        onChange={setQuantity}
        error={quantity !== '' && !valid}
        helperText={quantity !== '' && !valid ? t('products.transfer.range', { max: row.officeAvailable }) : ' '}
      />
    </StandardModal>
  );
};

export default ProductsByRoute;
